/**
 * Helpers for reading and persisting a user's per-cabinet view preferences.
 *
 * Preferences are stored per user, per cabinet, on the user's content:
 *  - `cabinetColumns[cabinetIdentifier].addedColumnIndexes` - ordered added columns
 *  - `cabinetSort[cabinetIdentifier]` - the active row sort ([{ direction, index }])
 *
 * This mirrors how saved filter views persist on `User.content.savedViews`, so a
 * user's customized columns and sort are restored on any browser or device they
 * log in from (web only).
 */

import { getJSONItem, removeItem } from './localStorage';
const CABINET_COLUMNS_KEY = 'cabinetColumns';
const ADDED_COLUMN_INDEXES_KEY = 'addedColumnIndexes';
const CABINET_SORT_KEY = 'cabinetSort';
const VALID_SORT_DIRECTIONS = new Set(['asc', 'desc']);

/**
 * Build the content path used with the user `change` action to persist the
 * ordered list of added column keys for a cabinet.
 */
export const cabinetColumnsChangePath = cabinetIdentifier => "/content/".concat(CABINET_COLUMNS_KEY, "/").concat(cabinetIdentifier, "/").concat(ADDED_COLUMN_INDEXES_KEY);

/**
 * Build the content path used with the user `change` action to persist the row
 * sort for a cabinet.
 */
export const cabinetSortChangePath = cabinetIdentifier => "/content/".concat(CABINET_SORT_KEY, "/").concat(cabinetIdentifier);

/**
 * Legacy browser-localStorage key used before per-user server persistence.
 */
export const legacyColumnsStorageKey = (customer, cabinetIdentifier) => "tru_".concat(customer, "_").concat(cabinetIdentifier, "_columns");

/**
 * Read added column keys from legacy localStorage (pre-server persistence).
 */
export const getLegacyAddedColumns = (customer, cabinetIdentifier) => {
  const stored = getJSONItem(legacyColumnsStorageKey(customer, cabinetIdentifier), {});
  const addedColumnIndexes = stored.addedColumnIndexes;
  if (!Array.isArray(addedColumnIndexes)) {
    return [];
  }
  return addedColumnIndexes.filter(key => typeof key === 'string');
};

/**
 * Remove legacy localStorage column preferences after migrating to user content.
 */
export const clearLegacyAddedColumns = (customer, cabinetIdentifier) => {
  removeItem(legacyColumnsStorageKey(customer, cabinetIdentifier));
};

/**
 * Read the ordered list of added column keys for a cabinet from user content.
 * Returns an empty array when nothing is stored or the stored value is invalid,
 * so callers fall back to the cabinet definition defaults.
 */
export const getAddedColumnsFromContent = (content, cabinetIdentifier) => {
  var _content$CABINET_COLU;
  const stored = content === null || content === void 0 || (_content$CABINET_COLU = content[CABINET_COLUMNS_KEY]) === null || _content$CABINET_COLU === void 0 || (_content$CABINET_COLU = _content$CABINET_COLU[cabinetIdentifier]) === null || _content$CABINET_COLU === void 0 ? void 0 : _content$CABINET_COLU[ADDED_COLUMN_INDEXES_KEY];
  if (!Array.isArray(stored)) {
    return [];
  }
  return stored.filter(key => typeof key === 'string');
};

/**
 * Read the persisted row sort for a cabinet from user content. Returns an empty
 * array when nothing is stored or the stored value is invalid, so callers fall
 * back to the cabinet's default ordering.
 */
export const getSortFromContent = (content, cabinetIdentifier) => {
  var _content$CABINET_SORT;
  const stored = content === null || content === void 0 || (_content$CABINET_SORT = content[CABINET_SORT_KEY]) === null || _content$CABINET_SORT === void 0 ? void 0 : _content$CABINET_SORT[cabinetIdentifier];
  if (!Array.isArray(stored)) {
    return [];
  }
  return stored.filter(entry => entry && typeof entry.index === 'string' && typeof entry.direction === 'string').map(entry => {
    const direction = entry.direction.toLowerCase();
    return VALID_SORT_DIRECTIONS.has(direction) ? {
      direction,
      index: entry.index
    } : null;
  }).filter(Boolean);
};

/**
 * Filter a sort array to indexes that are still valid for the cabinet columns.
 */
export const getRestorableSort = function getRestorableSort(sort) {
  let columnDefs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  if (!Array.isArray(sort) || sort.length === 0) {
    return [];
  }
  const validSortIndexes = new Set(columnDefs.filter(col => col.sortIndex).map(col => col.sortIndex));
  return sort.filter(entry => validSortIndexes.has(entry.index));
};